import { messages as apiViewsMessages } from '@databases/api-views/messages';
import { type DatabaseField, DatabaseFieldType } from '@databases/types/database';
import { BacklinkDisplayType } from '@databases/types/database';
import { defineMessages } from 'react-intl-next';

import { getJiraFieldEquivalentType } from '../field-types/jiraIssueDetails.ts';

const messages = defineMessages({
  'view-settings.sort-by-panel.alphabetical-ascending': {
    id: 'view-settings.sort-by-panel.alphabetical-ascending',
    defaultMessage: 'A → Z',
    description:
      'The label for the ascending sort direction for fields sorted alphabetically'
  },
  'view-settings.sort-by-panel.alphabetical-descending': {
    id: 'view-settings.sort-by-panel.alphabetical-descending',
    defaultMessage: 'Z → A',
    description:
      'The label for the descending sort direction for fields sorted alphabetically'
  },
  'view-settings.sort-by-panel.date-ascending': {
    id: 'view-settings.sort-by-panel.date-ascending',
    defaultMessage: 'Earliest → Latest',
    description: 'The label for the ascending sort direction for date-related fields'
  },
  'view-settings.sort-by-panel.date-descending': {
    id: 'view-settings.sort-by-panel.date-descending',
    defaultMessage: 'Latest → Earliest',
    description: 'The label for the descending sort direction for date-related fields'
  },
  'view-settings.sort-by-panel.numeric-ascending': {
    id: 'view-settings.sort-by-panel.numeric-ascending',
    defaultMessage: '0 → 9',
    description:
      'The label for the ascending sort direction for fields sorted numerically'
  },
  'view-settings.sort-by-panel.numeric-descending': {
    id: 'view-settings.sort-by-panel.numeric-descending',
    defaultMessage: '9 → 0',
    description:
      'The label for the descending sort direction for fields sorted numerically'
  },
  'view-settings.sort-by-panel.checkbox-ascending': {
    id: 'view-settings.sort-by-panel.checkbox-ascending',
    defaultMessage: 'Unchecked → Checked',
    description: 'The label for the ascending sort direction for checkbox fields'
  },
  'view-settings.sort-by-panel.checkbox-descending': {
    id: 'view-settings.sort-by-panel.checkbox-descending',
    defaultMessage: 'Checked → Unchecked',
    description: 'The label for the descending sort direction for checkbox fields'
  }
});

export type SortByDirection =
  | 'ascending'
  | 'descending'
  | 'customOrder'
  | 'reverseCustomOrder';

/**
 * Represents the effective sort label category for a field.
 */
export type SortCategory = 'alphabetical' | 'numeric' | 'date' | 'checkbox';

interface GetSortCategoryOptions {
  /**
   * For entry detail fields, the full resolved target field must be provided
   * so that its own settings are available for the recursive lookup.
   */
  resolvedTargetField?: DatabaseField;

  /**
   * For Jira issue details fields, the resolved Jira column type string used
   * to determine the correct sort label category.
   */
  jiraColumnType?: string;
}

/**
 * Resolves the effective sort category for a database field.
 */
export const getSortCategory = (
  field: DatabaseField,
  options?: GetSortCategoryOptions
): SortCategory => {
  const { type: fieldType, settings } = field;

  switch (fieldType) {
    case DatabaseFieldType.CHECKBOX:
      return 'checkbox';
    case DatabaseFieldType.DATE:
      return 'date';
    case DatabaseFieldType.NUMBER:
      return 'numeric';
    case DatabaseFieldType.PAGE_DETAILS: {
      const contentField = settings.contentField;
      if (contentField === 'createdAt' || contentField === 'updatedAt') {
        return 'date';
      }
      if (contentField === 'versionNumber') {
        return 'numeric';
      }
      return 'alphabetical';
    }
    case DatabaseFieldType.BACKLINK:
      return settings.backlinkDisplayType === BacklinkDisplayType.NUMBER
        ? 'numeric'
        : 'alphabetical';
    case DatabaseFieldType.ENTRY_DETAILS:
      return options?.resolvedTargetField
        ? getSortCategory(options.resolvedTargetField)
        : 'alphabetical';
    case DatabaseFieldType.JIRA_ISSUE_DETAILS: {
      const equivalentType = getJiraFieldEquivalentType(options?.jiraColumnType);
      if (equivalentType === DatabaseFieldType.DATE) {
        return 'date';
      }
      if (equivalentType === DatabaseFieldType.NUMBER) {
        return 'numeric';
      }
      return 'alphabetical';
    }
    default:
      return 'alphabetical';
  }
};

/**
 * Represents the variant of custom-order labels to display.
 */
export type CustomOrderVariant = 'default' | 'statusOrder';

interface GetLabelMessageOptions {
  direction: SortByDirection;
  sortCategory: SortCategory;
  customOrderVariant?: CustomOrderVariant;
}

export const getLabelMessage = ({
  direction,
  sortCategory,
  customOrderVariant = 'default'
}: GetLabelMessageOptions) => {
  if (direction === 'customOrder') {
    return customOrderVariant === 'statusOrder'
      ? apiViewsMessages['view-settings.sort.status-order']
      : apiViewsMessages['view-settings.sort.current-order'];
  }

  if (direction === 'reverseCustomOrder') {
    return customOrderVariant === 'statusOrder'
      ? apiViewsMessages['view-settings.sort.reverse-status-order']
      : apiViewsMessages['view-settings.sort.reverse-order'];
  }

  if (sortCategory === 'checkbox') {
    return direction === 'ascending'
      ? messages['view-settings.sort-by-panel.checkbox-ascending']
      : messages['view-settings.sort-by-panel.checkbox-descending'];
  }

  if (sortCategory === 'date') {
    return direction === 'ascending'
      ? messages['view-settings.sort-by-panel.date-ascending']
      : messages['view-settings.sort-by-panel.date-descending'];
  }

  if (sortCategory === 'numeric') {
    return direction === 'ascending'
      ? messages['view-settings.sort-by-panel.numeric-ascending']
      : messages['view-settings.sort-by-panel.numeric-descending'];
  }

  return direction === 'ascending'
    ? messages['view-settings.sort-by-panel.alphabetical-ascending']
    : messages['view-settings.sort-by-panel.alphabetical-descending'];
};

/**
 * Converts a sort direction to the data-model representation.
 */
export const sortByDirectionToDataModel = (
  direction: SortByDirection
): { ascending: boolean; customOrder?: boolean } => {
  switch (direction) {
    case 'customOrder':
      return { ascending: true, customOrder: true };
    case 'reverseCustomOrder':
      return { ascending: false, customOrder: true };
    case 'descending':
      return { ascending: false };
    case 'ascending':
    default:
      return { ascending: true };
  }
};

/**
 * Converts the data-model representation to a sort direction.
 */
export const dataModelToSortByDirection = (
  ascending: boolean,
  customOrder?: boolean
): SortByDirection => {
  if (customOrder) {
    return ascending ? 'customOrder' : 'reverseCustomOrder';
  }
  return ascending ? 'ascending' : 'descending';
};
